import Alert from "@/components/Alert";
import { Button } from "@/components/Button";
import { Modal } from "@/components/Dialog";
import LivlySwitch from "@/components/LivlySwitch";
import useLivlyGuest, {
  midnight,
  useGetLocksScheduleSettings,
  getFriendlyDayOfWeek,
  AccessControl,
  convertFriendlyDayOfWeekToEnum,
  LivlyLocksMeta
} from "@/context/GuestProvider";
import useLivlyUser from "@/context/UserProvider";
import { ServiceTypeEnum } from "@/types/Building";
import { DayOfWeek } from "@/types/User";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// ─── Types ───────────────────────────────────────────────────────────────────

type DaySelected = {
  startTime: number;
  endTime: number;
  day: string;
  disabled: boolean;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const MINUTES_IN_DAY = 1440;
const END_OF_DAY = 1439;

/** Normalize minutes: 1440 → 0, 1441 → 1, etc. */
const normalizeMinutes = (n: number) => n % MINUTES_IN_DAY;

/**
 * Mirror iOS getSelectedTimeRange logic.
 * Ensures start < end within a single day; corrects invalid ranges.
 */
const getSelectedTimeRange = (
  startTimeMinutes: number,
  endTimeMinutes: number
): { start: number; end: number } => {
  const start = normalizeMinutes(startTimeMinutes);
  const end = normalizeMinutes(endTimeMinutes);

  if (start < end) return { start, end };

  // Fallback: mirror iOS special cases
  if (start === END_OF_DAY || end === END_OF_DAY) {
    return { start: END_OF_DAY - 1, end: END_OF_DAY };
  }
  return { start, end: start + 1 };
};

/**
 * Get allowed setting start/end (normalized) for the restrict path.
 * Falls back to full day if overlap detected (mirrors iOS getAllowedTimeRange).
 */
const getAllowedRange = (
  settingTime: number,
  settingLength: number
): { start: number; end: number } => {
  const start = normalizeMinutes(settingTime);
  const end = normalizeMinutes(settingTime + settingLength);
  return start < end ? { start, end } : { start: 0, end: END_OF_DAY };
};

const getTimezoneDifference = (tz1: string, tz2: string): number => {
  const now = moment();
  return (now.tz(tz1).utcOffset() - now.tz(tz2).utcOffset()) / 60;
};

// ─── Component ───────────────────────────────────────────────────────────────

export const AccessScheduleModal = ({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const {
    user: {
      building,
      building: { timezone }
    }
  } = useLivlyUser();

  const { guest, updateGuest } = useLivlyGuest();
  const scheduleFromSettings = useGetLocksScheduleSettings();

  const [tempAccessControl, setTempAccessControl] =
    useState<AccessControl | null>(null);
  const [selectedDayOfWeek, setSelectedDayOfWeek] = useState<DayOfWeek | null>(
    null
  );

  const locksService = building.serviceTypes?.find(
    (st) => st.serviceType === ServiceTypeEnum.LivlyLocks
  );
  const metaData = locksService?.metaData
    ? (JSON.parse(locksService.metaData) as LivlyLocksMeta)
    : null;
  const isRestrictGuestKeyScheduleAccess =
    metaData?.restrictGuestKeyScheduleAccess;

  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const offset = getTimezoneDifference(browserTimezone, timezone);

  const currentSettingForSelectedDay = scheduleFromSettings.find(
    (d) => selectedDayOfWeek !== null && d.day === selectedDayOfWeek
  );
  const selectedDay =
    selectedDayOfWeek !== null
      ? tempAccessControl?.schedule?.days.find(
          (d) => d.day === getFriendlyDayOfWeek(selectedDayOfWeek)
        )
      : null;

  useEffect(() => {
    if (open) setTempAccessControl({ ...guest.accessControl });
  }, [open]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const updateScheduleDays = (updater: (d: DaySelected) => DaySelected) => {
    const days =
      tempAccessControl?.schedule?.days.map((d) =>
        d.day === getFriendlyDayOfWeek(selectedDayOfWeek!) ? updater(d) : d
      ) ?? [];
    setTempAccessControl({ ...tempAccessControl, schedule: { days } });
  };

  const handleDateTimeChange = (time: Date, type: "startTime" | "endTime") => {
    if (selectedDayOfWeek === null) return;

    const newTime = moment(time).diff(midnight, "minutes");

    if (!isRestrictGuestKeyScheduleAccess && selectedDay) {
      const start = type === "startTime" ? newTime : selectedDay.startTime;
      const end = type === "endTime" ? newTime : selectedDay.endTime;
      const fixed = getSelectedTimeRange(start, end);
      updateScheduleDays((d) => ({
        ...d,
        startTime: fixed.start,
        endTime: fixed.end
      }));
    } else {
      updateScheduleDays((d) => ({ ...d, [type]: newTime }));
    }
  };

  const handleReset = () => {
    const days = scheduleFromSettings.map((day) => ({
      day: getFriendlyDayOfWeek(day.day),
      startTime: day.time,
      endTime: day.time + day.length,
      disabled: day.disabled
    }));
    setTempAccessControl({
      schedule: { ...tempAccessControl?.schedule, days }
    });
  };

  const handleSetDisabled = (checked: boolean) => {
    updateScheduleDays((d) => ({ ...d, disabled: !checked }));
  };

  const onDone = () => {
    if (tempAccessControl)
      updateGuest({ ...guest, accessControl: tempAccessControl });
    onClose();
  };

  // ── Filter time helpers ────────────────────────────────────────────────────

  const toMoment = (minutes: number) =>
    moment()
      .startOf("day")
      .add(normalizeMinutes(minutes), "minutes")
      .add(-offset, "hour");

  const getRestrictFilterTime = (time: Date, type: "startTime" | "endTime") => {
    if (!currentSettingForSelectedDay || !selectedDay) return true;

    const { start, end } = getAllowedRange(
      currentSettingForSelectedDay.time,
      currentSettingForSelectedDay.length
    );

    const current = moment(time);
    const allowedStart = toMoment(start);
    const allowedEnd = toMoment(end);
    const inRange = current.isBetween(
      allowedStart,
      allowedEnd,
      undefined,
      "[]"
    );

    if (type === "startTime") {
      return inRange && current.isBefore(toMoment(selectedDay.endTime));
    }
    return inRange && current.isAfter(toMoment(selectedDay.startTime));
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const renderDayRow = (day: DaySelected) => (
    <div
      key={day.day}
      className="flex items-center py-4 border-b w-full cursor-pointer group"
      onClick={() =>
        setSelectedDayOfWeek(convertFriendlyDayOfWeekToEnum(day.day)!)
      }
    >
      <div className="w-44">
        <p className="text-lg text-gray-700 group-hover:text-red-300">
          {getFriendlyDayOfWeek(day?.day)}
        </p>
      </div>
      <div className="flex-1 flex justify-end text-gray-400 group-hover:text-red-300">
        {day?.disabled ? (
          <p>Off</p>
        ) : (
          <p>
            {moment()
              .startOf("day")
              .add(day?.startTime, "minutes")
              .format("LT")}
            {" - "}
            {moment().startOf("day").add(day?.endTime, "minutes").format("LT")}
          </p>
        )}
        <FontAwesomeIcon
          icon="chevron-right"
          className="text-sm text-gray-400 mx-2 group-hover:text-red-300"
        />
      </div>
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} size="lg">
      {/* Header */}
      <div className="relative flex items-center justify-center">
        {selectedDay && (
          <button
            className="absolute left-0 p-4"
            onClick={() => setSelectedDayOfWeek(null)}
          >
            <FontAwesomeIcon
              icon={["fas", "chevron-left"]}
              className="text-black"
            />
          </button>
        )}
        <div className="font-medium">Schedule Access</div>
      </div>

      {selectedDay ? (
        <div className="mt-4">
          {/* Allowed window hint */}
          {currentSettingForSelectedDay && (
            <p className="text-gray-500 mt-8">
              Pick a time window between{" "}
              {moment()
                .startOf("day")
                .add(currentSettingForSelectedDay.time, "minutes")
                .format("h:mm A")}
              {" and "}
              {moment()
                .startOf("day")
                .add(
                  currentSettingForSelectedDay.time +
                    currentSettingForSelectedDay.length,
                  "minutes"
                )
                .format("h:mm A")}
              .
            </p>
          )}

          {/* Day toggle */}
          <div className="flex items-center justify-between py-6">
            <p>{getFriendlyDayOfWeek(selectedDay.day)}</p>
            <LivlySwitch
              checked={!selectedDay.disabled}
              onChange={handleSetDisabled}
            />
          </div>

          {/* Time pickers */}
          {!selectedDay.disabled && (
            <div>
              <div className="border-y py-6 flex items-center">
                <p className="flex-1">From</p>
                <DatePicker
                  selected={toMoment(selectedDay.startTime).toDate()}
                  onChange={(time: Date) =>
                    handleDateTimeChange(time, "startTime")
                  }
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={1}
                  timeCaption="Time"
                  dateFormat="h:mm aa"
                  onKeyDown={(e: any) => e.preventDefault()}
                  filterTime={(time: Date) =>
                    isRestrictGuestKeyScheduleAccess
                      ? getRestrictFilterTime(time, "startTime")
                      : true
                  }
                />
              </div>
              <div className="py-6 flex items-center">
                <p className="flex-1">To</p>
                <DatePicker
                  selected={toMoment(selectedDay.endTime).toDate()}
                  onChange={(time: Date) =>
                    handleDateTimeChange(time, "endTime")
                  }
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={1}
                  timeCaption="Time"
                  dateFormat="h:mm aa"
                  onKeyDown={(e: any) => e.preventDefault()}
                  filterTime={(time: Date) =>
                    isRestrictGuestKeyScheduleAccess
                      ? getRestrictFilterTime(time, "endTime")
                      : true
                  }
                />
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-4">
          <Alert
            variant="info"
            message="Choose the times your guest can use Livly Key."
            hideIcon
          />
          <div className="mt-4">
            <p className="font-medium text-lg">Available times</p>
            <p className="text-sm text-gray-500">
              These times are limited based on your property's settings.
            </p>
          </div>
          <div className="mt-4">
            {tempAccessControl?.schedule?.days?.map(renderDayRow)}
          </div>
          <div className="mt-4 flex justify-between gap-4">
            <Button color="default" onClick={handleReset}>
              reset
            </Button>
            <Button onClick={onDone}>Done</Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
